// Path: ./src/utils/browseCategoryMergeGroups/index.ts
/**
 * Model + validation for the `frontend.browse_categories.merge_groups`
 * configuration, which lets several flyer item categories share one card on
 * the front-end browse-category page.
 *
 * The server stores it as JSON and validates it on save (rejecting a malformed
 * value with a 400 that names the offending group). The rules below mirror that
 * server-side validation so the admin sees the problem before saving, with one
 * deliberate exception: the server does NOT check that a category id exists, so
 * an id that no longer matches a category is surfaced as a warning here rather
 * than blocking the save.
 */

export const BROWSE_CATEGORY_MERGE_GROUPS_KEY =
  "frontend.browse_categories.merge_groups";

export const BROWSE_CATEGORY_MERGE_GROUPS_VERSION = 1;

export interface MergeGroup {
  key: string;
  primaryCategoryId: string;
  categoryIds: string[];
  label: string | null;
  labelChinese: string | null;
  labelFrench: string | null;
  sortOrder: number | null;
  enabled: boolean;
}

export interface MergeGroupsValue {
  version: number;
  groups: MergeGroup[];
}

export interface MergeGroupErrors {
  key?: string;
  label?: string;
  categoryIds?: string;
  primaryCategoryId?: string;
  sortOrder?: string;
}

export type ParseMergeGroupsResult =
  | { ok: true; value: MergeGroupsValue }
  | { ok: false; error: string };

const SORT_ORDER_STEP = 10;

const KEY_MAX_LENGTH = 60;

/** Key used when the source text carries no characters a slug can keep. */
const KEY_FALLBACK = "group";

const toSlugTokens = (value: string): string[] =>
  value
    .normalize("NFKD")
    // Drop the accent marks NFKD split off, so an accented name folds to ASCII.
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);

/** Joins tokens with dashes, dropping a whole token rather than cutting one in half. */
const joinSlugTokens = (tokens: string[]): string =>
  tokens.reduce((slug, tokenValue) => {
    if (!slug) {
      return tokenValue.slice(0, KEY_MAX_LENGTH);
    }
    const next = `${slug}-${tokenValue}`;
    return next.length > KEY_MAX_LENGTH ? slug : next;
  }, "");

/** Slugifies one piece of text: "Seafood & Meat" becomes "seafood-and-meat". */
export const slugifyMergeGroupKey = (value: string): string => {
  const tokens = toSlugTokens(value);

  if (!tokens.length) {
    // Text with nothing a slug can keep (Chinese-only, punctuation) still needs
    // a key; the suffix from uniqueMergeGroupKey keeps it distinct.
    return value.trim() ? KEY_FALLBACK : "";
  }

  return joinSlugTokens(tokens);
};

/**
 * Builds the slug stored as `key` from the card name followed by the names of
 * the categories on the card. The key is never typed by hand: the front-end
 * uses it for deep links and analytics, so it is generated once and then frozen
 * for the life of the group.
 *
 * A word already used is not repeated, which keeps the key readable when the
 * card name echoes its categories: card "Seafood" over "Seafood" and
 * "Seafood & Meat" gives `seafood-and-meat`, not
 * `seafood-seafood-seafood-and-meat`.
 */
export const buildMergeGroupKey = (
  cardName: string,
  categoryNames: string[],
): string => {
  const sources = [cardName, ...categoryNames];
  const seen = new Set<string>();
  const tokens: string[] = [];

  sources.forEach((source) => {
    toSlugTokens(source).forEach((tokenValue) => {
      if (!seen.has(tokenValue)) {
        seen.add(tokenValue);
        tokens.push(tokenValue);
      }
    });
  });

  if (!tokens.length) {
    return sources.some((source) => source.trim()) ? KEY_FALLBACK : "";
  }

  return joinSlugTokens(tokens);
};

/** Appends -2, -3, … until the key is free. */
export const uniqueMergeGroupKey = (
  base: string,
  taken: Set<string>,
): string => {
  if (!base || !taken.has(base)) {
    return base;
  }

  let suffix = 2;
  while (taken.has(`${base}-${suffix}`)) {
    suffix += 1;
  }

  return `${base}-${suffix}`;
};

export const emptyMergeGroupsValue = (): MergeGroupsValue => ({
  version: BROWSE_CATEGORY_MERGE_GROUPS_VERSION,
  groups: [],
});

/** Next free sort order, leaving gaps so a card can be inserted later. */
export const nextSortOrder = (groups: MergeGroup[]): number => {
  const used = groups
    .map((group) => group.sortOrder)
    .filter((order): order is number => typeof order === "number");

  if (!used.length) {
    return SORT_ORDER_STEP;
  }

  return Math.max(...used) + SORT_ORDER_STEP;
};

export const createMergeGroup = (groups: MergeGroup[]): MergeGroup => ({
  key: "",
  primaryCategoryId: "",
  categoryIds: [],
  label: null,
  labelChinese: null,
  labelFrench: null,
  sortOrder: nextSortOrder(groups),
  enabled: true,
});

const toOptionalText = (raw: unknown): string | null => {
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed ? trimmed : null;
};

const toOptionalNumber = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const toIdList = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  const ids = raw
    .filter((id): id is string => typeof id === "string")
    .map((id) => id.trim())
    .filter(Boolean);

  return Array.from(new Set(ids));
};

const normalizeGroup = (raw: Record<string, unknown>): MergeGroup => ({
  key: typeof raw.key === "string" ? raw.key.trim() : "",
  primaryCategoryId:
    typeof raw.primaryCategoryId === "string"
      ? raw.primaryCategoryId.trim()
      : "",
  categoryIds: toIdList(raw.categoryIds),
  label: toOptionalText(raw.label),
  labelChinese: toOptionalText(raw.labelChinese),
  labelFrench: toOptionalText(raw.labelFrench),
  sortOrder: toOptionalNumber(raw.sortOrder),
  enabled: raw.enabled === undefined ? true : Boolean(raw.enabled),
});

/**
 * Accepts the value as the API returns it (an object) or as the admin page
 * holds it (a pretty-printed JSON string) and normalizes it into editable
 * groups. A shape the editor cannot represent is reported instead of being
 * silently coerced, so a hand-written value is never quietly rewritten.
 */
export const parseMergeGroupsValue = (
  raw: unknown,
): ParseMergeGroupsResult => {
  let value: unknown = raw;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return { ok: true, value: emptyMergeGroupsValue() };
    }
    try {
      value = JSON.parse(trimmed);
    } catch {
      return { ok: false, error: "The saved value is not valid JSON." };
    }
  }

  if (value === null || value === undefined) {
    return { ok: true, value: emptyMergeGroupsValue() };
  }

  if (Array.isArray(value)) {
    return {
      ok: false,
      error:
        'The saved value is an array. It must be an object, for example {"version":1,"groups":[]}.',
    };
  }

  if (typeof value !== "object") {
    return {
      ok: false,
      error:
        'The saved value must be an object, for example {"version":1,"groups":[]}.',
    };
  }

  const record = value as Record<string, unknown>;
  const version =
    record.version === undefined
      ? BROWSE_CATEGORY_MERGE_GROUPS_VERSION
      : Number(record.version);

  if (version !== BROWSE_CATEGORY_MERGE_GROUPS_VERSION) {
    return {
      ok: false,
      error: `Unsupported version ${String(
        record.version,
      )}. This editor understands version ${BROWSE_CATEGORY_MERGE_GROUPS_VERSION}.`,
    };
  }

  const rawGroups = record.groups === undefined ? [] : record.groups;

  if (!Array.isArray(rawGroups)) {
    return { ok: false, error: '"groups" must be an array.' };
  }

  const malformed = rawGroups.some(
    (group) =>
      group === null || typeof group !== "object" || Array.isArray(group),
  );

  if (malformed) {
    return { ok: false, error: "Every entry in \"groups\" must be an object." };
  }

  return {
    ok: true,
    value: {
      version,
      groups: (rawGroups as Record<string, unknown>[]).map(normalizeGroup),
    },
  };
};

/**
 * Mirrors the server-side rules. Returns per-group errors keyed by group index;
 * an empty object means the value is safe to save.
 */
export const validateMergeGroups = (
  groups: MergeGroup[],
): Record<number, MergeGroupErrors> => {
  const errors: Record<number, MergeGroupErrors> = {};

  const setError = (
    index: number,
    field: keyof MergeGroupErrors,
    message: string,
  ) => {
    if (!errors[index]) {
      errors[index] = {};
    }
    if (!errors[index][field]) {
      errors[index][field] = message;
    }
  };

  const keyCounts = new Map<string, number>();
  const categoryOwners = new Map<string, number[]>();

  groups.forEach((group, index) => {
    const key = group.key.trim();
    if (key) {
      keyCounts.set(key, (keyCounts.get(key) ?? 0) + 1);
    }

    group.categoryIds.forEach((categoryId) => {
      categoryOwners.set(categoryId, [
        ...(categoryOwners.get(categoryId) ?? []),
        index,
      ]);
    });
  });

  groups.forEach((group, index) => {
    const key = group.key.trim();

    if (!key) {
      setError(
        index,
        "key",
        "No key yet — enter a card name and pick the categories, the key is generated from them.",
      );
    } else if ((keyCounts.get(key) ?? 0) > 1) {
      // Generated keys are de-duplicated, so this only shows up for a value
      // hand-written before the server validated it. Keys cannot be edited, so
      // the way out is to remove one group and add it again.
      setError(
        index,
        "key",
        `The key "${key}" is used by another group. Remove one of them and add it again to get a fresh key.`,
      );
    }

    if (!group.label?.trim()) {
      setError(index, "label", "A card name is required.");
    }

    if (!group.categoryIds.length) {
      setError(index, "categoryIds", "Pick at least one category.");
    }

    if (!group.primaryCategoryId) {
      setError(index, "primaryCategoryId", "Pick the primary category.");
    } else if (!group.categoryIds.includes(group.primaryCategoryId)) {
      setError(
        index,
        "primaryCategoryId",
        "The primary category must also be one of the categories in this card.",
      );
    }

    const shared = group.categoryIds.filter(
      (categoryId) => (categoryOwners.get(categoryId) ?? []).length > 1,
    );

    if (shared.length) {
      setError(
        index,
        "categoryIds",
        "A category can belong to only one group. Remove it from one of the groups.",
      );
    }

    if (group.sortOrder !== null && !Number.isFinite(group.sortOrder)) {
      setError(index, "sortOrder", "Sort order must be a number.");
    }
  });

  return errors;
};

export const hasMergeGroupErrors = (
  errors: Record<number, MergeGroupErrors>,
): boolean => Object.keys(errors).length > 0;

/**
 * Category ids that are not in the given set of known categories. The server
 * accepts them (so a deleted category cannot hard-fail the config) but the
 * front-end skips them, which is exactly the silent failure this editor exists
 * to prevent — so they are surfaced as a warning.
 */
export const findUnknownCategoryIds = (
  groups: MergeGroup[],
  knownCategoryIds: Set<string>,
): string[] => {
  const unknown = new Set<string>();

  groups.forEach((group) => {
    group.categoryIds.forEach((categoryId) => {
      if (!knownCategoryIds.has(categoryId)) {
        unknown.add(categoryId);
      }
    });
  });

  return Array.from(unknown);
};

/** Builds the exact JSON body the configuration endpoint expects. */
export const toMergeGroupsPayload = (
  groups: MergeGroup[],
): Record<string, unknown> => ({
  version: BROWSE_CATEGORY_MERGE_GROUPS_VERSION,
  groups: groups.map((group) => ({
    key: group.key.trim(),
    primaryCategoryId: group.primaryCategoryId,
    categoryIds: group.categoryIds,
    label: toOptionalText(group.label),
    labelChinese: toOptionalText(group.labelChinese),
    labelFrench: toOptionalText(group.labelFrench),
    ...(group.sortOrder === null ? {} : { sortOrder: group.sortOrder }),
    enabled: group.enabled,
  })),
});
